import { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Switch,
  useToast,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  useDisclosure,
  Input,
} from '@chakra-ui/react';
import { useGlobalAccess } from '../../context/GlobalAccessContext';

const SLACK_MESSAGE = `To view the aviation livestream on your phone or tablet:\n\nOpen your browser and go to slack.westchesterrtc.com\n\nWorkspace: dps-rtc\n\nLog in with your Slack credentials\n\nTap WCPD Air3 Downlink or WCPD Air2 Downlink to start the stream\n\nNote: The feed may be delayed up to 10 seconds from real time.`;

export function Settings() {
  const { globalAviationAccess, refreshGlobalAccess } = useGlobalAccess();
  const [updating, setUpdating] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [showSlackModal, setShowSlackModal] = useState(false);
  const prevAccess = useRef(globalAviationAccess);

  // Show modal only when toggled ON (not on page load)
  useEffect(() => {
    if (!prevAccess.current && globalAviationAccess) {
      setShowSlackModal(true);
    }
    prevAccess.current = globalAviationAccess;
  }, [globalAviationAccess]);

  const updateGlobalAccess = async (enabled: boolean) => {
    try {
      setUpdating(true);
      const response = await fetch('/api/settings/global-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ enabled })
      });
      
      if (response.ok) {
        const data = await response.json();
        await refreshGlobalAccess(); // Refresh the context
        toast({
          title: 'Settings updated',
          description: data.message,
          status: 'success',
          duration: 3000
        });
      } else {
        throw new Error('Failed to update global access settings');
      }
    } catch (error) {
      toast({
        title: 'Error updating settings',
        description: 'Failed to update global access settings',
        status: 'error',
        duration: 3000
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(SLACK_MESSAGE);
    toast({
      title: 'Copied!',
      description: 'Message copied to clipboard.',
      status: 'success',
      duration: 2000
    });
  };

  return (
    <Box 
      width="100%"
      p={[4, 6, 8]}
    >
      <VStack spacing={6} align="stretch">
        <Heading size="lg" mb={4}>System Settings</Heading>
        
        <Card>
          <CardHeader>
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Heading size="md">Global Access Control</Heading>
                <Text fontSize="sm" color="gray.600">
                  Manage system-wide access permissions
                </Text>
              </VStack>
              <Badge 
                colorScheme={globalAviationAccess ? "green" : "gray"}
                variant="subtle"
                fontSize="sm"
              >
                {globalAviationAccess ? "ENABLED" : "DISABLED"}
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" p={4} bg="gray.50" borderRadius="md">
                <VStack align="start" spacing={1}>
                  <Text fontWeight="semibold">Global Aviation Access</Text>
                  <Text fontSize="sm" color="gray.600">
                    When enabled, all users can access aviation streams regardless of individual permissions
                  </Text>
                </VStack>
                <Switch
                  isChecked={globalAviationAccess}
                  onChange={(e) => updateGlobalAccess(e.target.checked)}
                  isDisabled={updating}
                  colorScheme="blue"
                  size="lg"
                />
              </HStack>
              
              {globalAviationAccess && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Global Access Active</AlertTitle>
                    <AlertDescription>
                      All users can now access aviation streams. Individual user permissions are temporarily overridden.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
              
              <Divider />
              
              <Text fontSize="sm" color="gray.600">
                <strong>Note:</strong> This setting provides emergency access to aviation streams for all users. 
                Use this feature only when necessary and remember to disable it when no longer needed.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Slack Copy Modal */}
      <Modal isOpen={showSlackModal} onClose={() => setShowSlackModal(false)} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Share Aviation Access Instructions</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4} whiteSpace="pre-line">
              To view the aviation livestream on your phone or tablet:

Open your browser and go to slack.westchesterrtc.com

Workspace: dps-rtc

Log in with your Slack credentials

Tap WCPD Air3 Downlink or WCPD Air2 Downlink to start the stream

Note: The feed may be delayed up to 10 seconds from real time.
            </Text>
            <Button colorScheme="blue" onClick={handleCopy} mb={2}>
              Copy to Clipboard
            </Button>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setShowSlackModal(false)} variant="ghost">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Settings; 